import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useParams, useHistory } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar
} from '@material-ui/core'
import {
  ArrowBack,
  GetApp,
  FileCopy
} from '@material-ui/icons'
import OutputTable from './OutputTable'
import BidEstimationHeader from '../shared/BidEstimationHeader'

const BidOutputView = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const [project, setProject] = React.useState(null)
  const [selectedBuilding, setSelectedBuilding] = React.useState('')
  const [outputData, setOutputData] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [projectLoading, setProjectLoading] = React.useState(false)
  const [copyNotice, setCopyNotice] = React.useState(false)

  React.useEffect(() => {
    fetchProject()
  }, [projectId])

  React.useEffect(() => {
    if (selectedBuilding) {
      fetchOutput(selectedBuilding)
    } else {
      setOutputData(null)
    }
  }, [selectedBuilding])

  const fetchProject = () => {
    setProjectLoading(true)
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}`).then(res => {
      setProject(res.data)
      setProjectLoading(false)
      // Auto-select first building if available
      const buildings = res.data?.buildings || []
      if (buildings.length > 0 && !selectedBuilding) {
        setSelectedBuilding(buildings[0].name || buildings[0])
      }
    }).catch(err => {
      console.error(err)
      setProjectLoading(false)
    })
  }

  const fetchOutput = (building) => {
    setLoading(true)
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/output/${encodeURIComponent(building)}`).then(res => {
      setOutputData(res.data)
      setLoading(false)
    }).catch(err => {
      console.error(err)
      setLoading(false)
    })
  }

  const handleExport = () => {
    window.open(
      `${props.url}/api/bid-estimation/projects/${projectId}/export/${encodeURIComponent(selectedBuilding)}`,
      '_blank'
    )
  }

  const handleCopyValues = () => {
    if (!outputData || !outputData.items) return
    const lines = outputData.items.map(item => {
      const unitCost = item.unitCost || ''
      const totalCost = item.totalCost || ''
      return `${unitCost}\t${totalCost}`
    })
    const text = lines.join('\n')
    navigator.clipboard.writeText(text).then(() => {
      setCopyNotice(true)
    }).catch(err => {
      console.error('Copy failed:', err)
    })
  }

  const buildings = project?.buildings || []
  const buildingNames = buildings.map(b => b.name || b)

  if (projectLoading) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress />
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: 20 }}>
      <BidEstimationHeader
        backTo={`/bid-estimation/${projectId}`}
        backLabel="Project"
        breadcrumbs={[
          { label: 'Bid Estimation', to: '/bid-estimation' },
          { label: project?.name || 'Project', to: `/bid-estimation/${projectId}` },
          { label: 'Output' }
        ]}
        title={`Output${project?.name ? ' · ' + project.name : ''}`}
      />

      {/* Controls */}
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 20 }}>
        <Grid item>
          <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
            <InputLabel>Building</InputLabel>
            <Select
              value={selectedBuilding}
              onChange={e => setSelectedBuilding(e.target.value)}
              label="Building"
            >
              {buildingNames.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetApp />}
            onClick={handleExport}
            disabled={!selectedBuilding || !outputData}
          >
            Export to Excel
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            startIcon={<FileCopy />}
            onClick={handleCopyValues}
            disabled={!outputData || !outputData.items}
          >
            Copy Values
          </Button>
        </Grid>
      </Grid>

      {/* Output table */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress />
        </div>
      ) : (
        <OutputTable data={outputData} />
      )}

      <Snackbar
        open={copyNotice}
        autoHideDuration={2000}
        onClose={() => setCopyNotice(false)}
        message="Unit cost and total cost values copied to clipboard"
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(BidOutputView)
